import { useCallback, useEffect, useState } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_URL = '/version.json';

/**
 * Polls /version.json (written at build time) and reports whether a newer
 * build has been deployed than the one this tab is running. Checks on mount,
 * on an interval, and whenever the tab becomes visible again.
 */
export default function useVersionCheck(): { updateAvailable: boolean; reload: () => void } {
    // Dev only: add ?showUpdateNotice to the URL to preview the notice.
    const [updateAvailable, setUpdateAvailable] = useState(
        () => __BUILD_ID__ === 'dev' && new URLSearchParams(window.location.search).has('showUpdateNotice')
    );

    useEffect(() => {
        // Dev builds have no meaningful version; nothing to compare.
        if (__BUILD_ID__ === 'dev' || updateAvailable) {
            return;
        }

        let cancelled = false;

        const check = async () => {
            try {
                const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
                if (!response.ok) {
                    return;
                }
                const { buildId } = await response.json();
                if (!cancelled && buildId && buildId !== __BUILD_ID__) {
                    setUpdateAvailable(true);
                }
            } catch {
                // Offline or server mid-deploy; try again next time.
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                check();
            }
        };

        check();
        const timer = window.setInterval(check, CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [updateAvailable]);

    const reload = useCallback(() => window.location.reload(), []);

    return { updateAvailable, reload };
}
